import { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import SignUpPage from './pages/SignUpPage'
import SignInPage from './pages/SignInPage'
import DashboardPage from './pages/DashboardPage'
import CoopsPage from './pages/CoopsPage'
import { CoopDetailPage } from './pages/CoopDetailPage'
import FlocksPage from './pages/FlocksPage'
import { FlockDetailPage } from './pages/FlockDetailPage'
import { FlockHistoryPage } from './features/flocks/components/FlockHistoryPage'
import { RecordsPage } from './pages/RecordsPage'
import { PurchasesPage } from './features/purchases/pages/PurchasesPage'
import { SettingsPage } from './pages/SettingsPage'
import NotFoundPage from './pages/NotFoundPage'
import ProtectedRoute from './components/ProtectedRoute'
import { BottomNavigation } from './components/BottomNavigation'
import { OfflineBanner, PwaInstallPrompt, IosInstallPrompt, UpdateNotification } from './shared/components'
import { useApiClient } from './lib/useApiClient'
import { useAuth } from '@clerk/clerk-react'
import { startAutoSync } from './lib/syncManager'
import { Box, AppBar, Toolbar, Typography, IconButton, Avatar } from '@mui/material'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { AppBarProvider, useAppBar } from './context/AppBarContext'
import { useUser } from '@clerk/clerk-react'
import { UserSettingsProvider } from './features/settings'

function AppInner() {
  useApiClient()
  const { isSignedIn } = useAuth()
  const { user } = useUser()
  const navigate = useNavigate()
  const [bannerVisible, setBannerVisible] = useState(false)
  const { title, onBack } = useAppBar()

  useEffect(() => {
    startAutoSync()
  }, [])

  return (
    <>
      {/* App Bar — only shown when signed in */}
      {isSignedIn && (
        <AppBar
          position="sticky"
          color="default"
          elevation={1}
          sx={{ top: 0, zIndex: 1100, height: 64 }}
        >
          <Toolbar sx={{ minHeight: '64px !important', px: 2 }}>
            {onBack ? (
              <IconButton
                edge="start"
                onClick={onBack}
                aria-label="back"
                size="medium"
                sx={{ mr: 1, p: 1 }}
              >
                <ArrowBackIcon />
              </IconButton>
            ) : null}
            <Typography
              variant="h6"
              component="div"
              sx={{ flexGrow: 1, fontWeight: 700, color: 'primary.main', letterSpacing: 0.5 }}
            >
              {title ?? 'Chickquita'}
            </Typography>
            <IconButton
              onClick={() => navigate('/settings')}
              aria-label="settings"
              size="medium"
              sx={{ p: 1 }}
            >
              {user?.imageUrl
                ? <Avatar src={user.imageUrl} sx={{ width: 32, height: 32 }} />
                : <AccountCircleIcon />}
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      {/* PWA update notification */}
      <UpdateNotification />

      {/* Offline detection banner */}
      {isSignedIn && <OfflineBanner onVisibilityChange={setBannerVisible} />}

      {/* PWA Install prompts (auto-detect platform) */}
      {isSignedIn && (
        <>
          <PwaInstallPrompt />
          <IosInstallPrompt />
        </>
      )}

      <Box sx={{ pb: isSignedIn ? 'calc(64px + env(safe-area-inset-bottom) + 80px)' : 0, pt: bannerVisible ? '60px' : 0 }}>
        <Routes>
          <Route path="/" element={<Navigate to="/sign-up" replace />} />
          <Route path="/sign-up/*" element={<SignUpPage />} />
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/statistics"
            element={<Navigate to="/records/stats" replace />}
          />
          <Route
            path="/records/*"
            element={
              <ProtectedRoute>
                <RecordsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coops"
            element={
              <ProtectedRoute>
                <CoopsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coops/:id"
            element={
              <ProtectedRoute>
                <CoopDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coops/:coopId/flocks"
            element={
              <ProtectedRoute>
                <FlocksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coops/:coopId/flocks/:flockId"
            element={
              <ProtectedRoute>
                <FlockDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coops/:coopId/flocks/:flockId/history"
            element={
              <ProtectedRoute>
                <FlockHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/daily-records"
            element={<Navigate to="/records/list" replace />}
          />
          <Route
            path="/purchases"
            element={
              <ProtectedRoute>
                <PurchasesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Box>
      {isSignedIn && <BottomNavigation />}
    </>
  )
}

function App() {
  return (
    <AppBarProvider>
      <UserSettingsProvider>
        <AppInner />
      </UserSettingsProvider>
    </AppBarProvider>
  )
}

export default App
